import { Text, TextMarkup } from '../text';
import React, { useState, useMemo, useEffect } from 'react';
import { TypographyColor } from '../../objects';
import clsx from 'clsx';
import { is } from '../../services';
import { hashClass } from '../../helpers';
import { useTrilogyContext } from '../../context';
/**
 * OTP Code Component
 * @param className {string} Additionnal css classes
 * @param code {string} Code Text Input
 * @param codeSize {number} Code Size Number
 * @param disabled {boolean} Disabled OTP Code Input
 * @param error {boolean} OTP Code Input has error | Display error icon
 * @param onCompleted {Function} Return code input string
 * @param activated {boolean} Activated OTP
 * @param onChange {Function} onChange Input return current code
 * @param onFocus {Function} onFocus return if focused opt
 * @param autoFocus {boolean} Should auto focus otp
 */
const Otp = ({ className, code, codeSize = 6, disabled, error, onCompleted, onChange, onFocus, activated, label, errorMessage, autoFocus, ...others }) => {
    const [codeInput, setCodeInput] = useState(code || '');
    const [_focused, setFocused] = useState(false);
    const { styled } = useTrilogyContext();
    const testDigit = /^-?\d*\.?\d*$/;
    const classes = hashClass(styled, clsx('otp-list', error && is('error'), activated && is('tertiary'), disabled && is('disabled'), className));
    useEffect(() => {
        if (!disabled && codeInput.length > 0) {
            onChange === null || onChange === void 0 ? void 0 : onChange(codeInput);
        }
        if (!disabled && codeInput.length >= codeSize) {
            onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted(codeInput);
        }
    }, [codeSize, codeInput, disabled, onChange, onCompleted]);
    const valueItems = useMemo(() => {
        const valueArray = codeInput.split('');
        const items = [];
        for (let i = 0; i < codeSize; i++) {
            const char = valueArray[i];
            if (testDigit.test(char)) {
                items.push(char);
            }
            else {
                items.push('');
            }
        }
        return items;
    }, [codeInput, codeSize, testDigit]);
    const focusToNextInput = (target) => {
        const nextElementSibling = target.nextElementSibling;
        if (nextElementSibling) {
            nextElementSibling.focus();
        }
    };
    const focusToPrevInput = (target) => {
        const previousElementSibling = target.previousElementSibling;
        if (previousElementSibling) {
            previousElementSibling.focus();
        }
    };
    const inputOnChange = (e, idx) => {
        const { target } = e;
        let targetValue = target.value.trim();
        const isTargetValueDigit = testDigit.test(targetValue);
        if (!isTargetValueDigit && targetValue !== '') {
            return;
        }
        const newValue = codeInput.substring(0, idx) + targetValue + codeInput.substring(idx + 1);
        setCodeInput(newValue);
        if (isTargetValueDigit) {
            focusToNextInput(target);
        }
    };
    const inputOnKeyDown = (e) => {
        const { key } = e;
        const target = e.target;
        if (key === 'ArrowRight' || key === 'ArrowDown') {
            e.preventDefault();
            return focusToNextInput(target);
        }
        if (key === 'ArrowLeft' || key === 'ArrowUp') {
            e.preventDefault();
            return focusToPrevInput(target);
        }
        const targetValue = target.value;
        target.setSelectionRange(0, targetValue.length);
        if (e.key !== 'Backspace' || targetValue !== '') {
            target.value = '';
            return;
        }
        focusToPrevInput(target);
    };
    const inputOnFocus = (e) => {
        const { target } = e;
        const prevInputEl = target.previousElementSibling;
        if (prevInputEl && prevInputEl.value === '') {
            return prevInputEl.focus();
        }
        target.setSelectionRange(0, target.value.length);
    };
    return (React.createElement(React.Fragment, null,
        label && (React.createElement(Text, { markup: TextMarkup.P, typo: (disabled && TypographyColor.TEXT_GREY) ||
                (error && TypographyColor.TEXT_ERROR) ||
                TypographyColor.TEXT_TERTIARY }, label)),
        React.createElement("div", { "data-testid": 'otp-input', className: classes, onClick: () => {
                if (!disabled) {
                    setFocused(true);
                    onFocus === null || onFocus === void 0 ? void 0 : onFocus(true);
                }
            } }, valueItems.map((digit, idx) => (React.createElement("input", { key: idx, type: 'text', inputMode: 'numeric', autoComplete: 'one-time-code', autoFocus: idx === 0 && autoFocus, pattern: '\\d{1}', maxLength: codeSize, className: 'otp', value: digit, onKeyDown: inputOnKeyDown, onFocus: inputOnFocus, onChange: (e) => inputOnChange(e, idx), disabled: disabled, ...others })))),
        errorMessage && (React.createElement(Text, { className: hashClass(styled, clsx('otp-error-message')), markup: TextMarkup.P, typo: (error && TypographyColor.TEXT_ERROR) || TypographyColor.TEXT_TERTIARY }, errorMessage))));
};
export default Otp;
//# sourceMappingURL=Otp.js.map